import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Skeleton,
  Table, TableBody, TableCell, TableHead, TableRow, Paper, alpha
} from '@mui/material';
import {
  TrendingUp, People, Description, Receipt, Payment,
  Warning, ArrowUpward, ArrowDownward
} from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { API } from '../context/AuthContext';
import { fmt, STATUS_COLORS, STATUS_LABELS } from '../utils';
import { useNavigate } from 'react-router-dom';

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function KPICard({ title, value, subtitle, icon, color, loading }) {
  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', top: -20, right: -20, width: 100, height: 100,
        borderRadius: '50%', background: alpha(color, 0.08)
      }} />
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {title}
            </Typography>
            {loading ? <Skeleton width={120} height={40} /> :
              <Typography variant="h4" sx={{ fontWeight: 800, color, mt: 0.5, lineHeight: 1 }}>
                {value}
              </Typography>
            }
            {subtitle && <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>{subtitle}</Typography>}
          </Box>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px',
            background: alpha(color, 0.12),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: color
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/dashboard/stats').then(r => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  const chartData = stats?.monthlyRevenue?.map(d => ({
    name: MONTH_NAMES[d._id.month - 1],
    cobrado: d.total
  })) || [];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E1B2E' }}>Panel de Control</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {new Date().toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard title="Clientes activos" value={loading ? '-' : stats?.clients?.total}
            subtitle={`+${stats?.clients?.newThisMonth || 0} este mes`}
            icon={<People />} color="#6B21A8" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard title="Cobrado este mes" value={loading ? '-' : fmt.currency(stats?.payments?.thisMonth?.total)}
            subtitle={`${stats?.payments?.thisMonth?.count || 0} pagos`}
            icon={<TrendingUp />} color="#10B981" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard title="Por cobrar" value={loading ? '-' : fmt.currency(stats?.invoices?.pending?.total)}
            subtitle={`${stats?.invoices?.pending?.count || 0} facturas pendientes`}
            icon={<Receipt />} color="#F59E0B" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard title="Cotizaciones abiertas" value={loading ? '-' : stats?.quotes?.open}
            subtitle={`${fmt.currency(stats?.quotes?.thisMonth?.total)} generado`}
            icon={<Description />} color="#3B82F6" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard title="Cotizaciones vencidas" value={loading ? '-' : (stats?.quotes?.expired || 0)}
            subtitle="Sin respuesta del cliente"
            icon={<Warning />} color="#F59E0B" loading={loading} />
        </Grid>
      </Grid>

      {/* Alert for overdue */}
      {stats?.invoices?.overdue > 0 && (
        <Card sx={{ mb: 2, border: '1px solid #FEF3C7', background: '#FFFBEB' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '12px !important' }}>
            <Warning sx={{ color: '#F59E0B' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400E' }}>
              Tienes {stats.invoices.overdue} factura(s) vencida(s) — revisa el módulo de facturas
            </Typography>
          </CardContent>
        </Card>
      )}
      {!loading && (stats?.quotes?.expired || 0) > 0 && (
        <Card sx={{ mb: 3, border: '1px solid #FDE68A', background: '#FFFBEB' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '12px !important' }}>
            <Warning sx={{ color: '#D97706' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400E' }}>
              Tienes {stats.quotes.expired} cotización(es) vencida(s) sin respuesta — considera dar seguimiento
            </Typography>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2.5}>
        {/* Revenue chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Ingresos cobrados (últimos 6 meses)</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorCobrado" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#9333EA" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#9333EA" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false}
                    tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmt.currency(v)} />
                  <Area type="monotone" dataKey="cobrado" stroke="#9333EA" strokeWidth={2.5}
                    fill="url(#colorCobrado)" name="Cobrado" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent activity */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Últimos pagos</Typography>
              {loading ? <Skeleton height={200} /> : stats?.recent?.payments?.map(p => (
                <Box key={p._id} sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  py: 1.2, borderBottom: '1px solid #F3F4F6'
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.client?.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.number}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#10B981' }}>
                    {fmt.currency(p.amount)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent quotes */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Cotizaciones recientes</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Número</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={4}><Skeleton /></TableCell></TableRow>) :
                    stats?.recent?.quotes?.map(q => (
                      <TableRow key={q._id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/cotizaciones/${q._id}/editar`)}
                      >
                        <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{q.number}</TableCell>
                        <TableCell>{q.clientSnapshot?.name || q.client?.name}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt.currency(q.total)}</TableCell>
                        <TableCell>
                          <Chip label={STATUS_LABELS[q.status]} color={STATUS_COLORS[q.status]} size="small" />
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent invoices */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Facturas pendientes</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Número</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={4}><Skeleton /></TableCell></TableRow>) :
                    stats?.recent?.invoices?.map(inv => (
                      <TableRow key={inv._id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/facturas/${inv._id}/editar`)}
                      >
                        <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{inv.number}</TableCell>
                        <TableCell>{inv.clientSnapshot?.name || inv.client?.name}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#F59E0B' }}>{fmt.currency(inv.balance)}</TableCell>
                        <TableCell>
                          <Chip label={STATUS_LABELS[inv.status]} color={STATUS_COLORS[inv.status]} size="small" />
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
